"use client"
import React, { useState, useEffect } from "react";
import useApi from "@/components/api/useApi";
import LoadingAnimation from "@/components/shared/LoadingAnimation";
import CommunityDirectoryMapAndPins from "@/components/communities/CommunityDirectoryMapAndPins";
import CommunityDirectoryList from "@/components/communities/CommunityDirectoryList";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

const CommunityDirectoryView = ({
  community,
  name,
  types,
  showMap,
  external,
  scrollable,
  includeCoverPhoto = false,
  publicTags = null,
  showSearchFilter = true,
  showOpenNow = true,
  systemLayerTypes = null,
  token
}) => {
  const api = useApi();

  const [loaded, setLoaded] = useState(false);
  const [denied, setDenied] = useState(false);
  const [points, setPoints] = useState([]);

  const { currentLocation } = useCurrentLocation();

  useEffect(() => {
    if (community && name) {
      const criteria = publicTags && publicTags.length > 0
        ? [{ mapToField: "BUSINESS_PUBLIC_TAGS", operand: "IN", value: publicTags }]
        : [];

      // businesses and organizations are listed in separate directory sections
      const object = types && types.length === 1 && types[0] === "ORGANIZATION" ? "ORGANIZATION" : "BUSINESS";
      const payload = {
        object,
        operation: "LIST",
        criteria,
        max: 500
      };

      Promise.all([
        api.crm_publicDataQuery(name, payload, token),
        api.listCommunityAccolades(name)
      ])
        .then(([profiles, accolades]) => {
          const transformed = profiles.flatMap(profile => {
            if (accolades && accolades.length > 0 && profile.communityAccoladeIds && profile.communityAccoladeIds.length > 0) {
              profile.accolades = accolades.filter(a =>
                profile.communityAccoladeIds.includes(a.id)
              );
            }

            const locations =
              profile.locations && Array.isArray(profile.locations)
                ? profile.locations
                : [];

            if (locations.length > 0) {
              return locations.map(location => ({
                type: "USER_PROFILE",
                id: location.id,
                location,
                userProfile: { ...profile, location }
              }));
            }

            if (showMap) {
              // Profiles with no locations can't be plotted on a map.
              return [];
            }

            return [
              {
                type: "USER_PROFILE",
                id: profile.id,
                location: null,
                userProfile: { ...profile, location: null }
              }
            ];
          });

          transformed.sort((a, b) =>
            (a.userProfile.displayName ?? "").localeCompare(b.userProfile.displayName ?? "")
          );

          setPoints(transformed);
          setLoaded(true);
        })
        .catch(() => {
          setLoaded(true);
        });
    }
  }, [community, name, publicTags]);

  return (
    <>
      {loaded && denied && <></>}
      {loaded && !denied && (
        <>
          {showMap ? (
            <CommunityDirectoryMapAndPins
              currentLocation={currentLocation}
              community={community}
              points={points}
              types={types}
              showMap={showMap}
              scrollable={scrollable}
              external={external}
              showCoverPhoto={includeCoverPhoto}
              showSearchFilter={showSearchFilter}
              showOpenNow={showOpenNow}
              name={name}
              systemLayerTypes={systemLayerTypes}
              token={token}
            />
          ) : (
            <CommunityDirectoryList
              currentLocation={currentLocation}
              community={community}
              points={points}
              types={types}
              external={external}
              showCoverPhoto={includeCoverPhoto}
              showSearchFilter={showSearchFilter}
              showOpenNow={showOpenNow}
            />
          )}
        </>
      )}
      {!loaded && (
        <div style={{ padding: "4rem" }}>
          <LoadingAnimation />
        </div>
      )}
    </>
  );
};
export default CommunityDirectoryView;
